import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { commissionAPI, paymentRequestAPI } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import { formatCurrency } from '../../utils/currency';
import { DollarSign, TrendingUp, TrendingDown, Send, Calendar } from 'lucide-react';
import PageHeader from './components/PageHeader';
import './CommissionPage.css';

const CommissionPage = () => {
  const navigate = useNavigate();
  const { language } = useAuth();
  const { t } = useTranslation(language);
  const currentDate = new Date();
  const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  const [commissionData, setCommissionData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCommissionDetails();
  }, [selectedMonth, selectedYear]);

  const fetchCommissionDetails = async () => {
    setLoading(true);
    try {
      const [commissionRes, requestsRes] = await Promise.all([
        commissionAPI.getDetails(selectedMonth, selectedYear),
        paymentRequestAPI.getAll(),
      ]);

      console.log('Commission API response:', commissionRes.data);
      console.log('Payment Requests API response:', requestsRes.data);

      // API returns { data: { month, year, commissions, clawbacks, totalEarned, totalClawback, netTotal, availableBalance, ... } }
      // response.data.data because axios wraps in { data: {...} } and our API also returns { data: {...} }
      const apiData = commissionRes.data.data || commissionRes.data;
      const requestsData = requestsRes.data?.data || requestsRes.data || [];

      // Calculate total paid and pending for selected month (for display purposes)
      const totalPaid = requestsData
        .filter(req => req.month === selectedMonth && req.year === selectedYear && req.status === 'paid')
        .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);

      const totalPending = requestsData
        .filter(req => req.month === selectedMonth && req.year === selectedYear && ['pending', 'approved'].includes(req.status))
        .reduce((sum, req) => sum + parseFloat(req.amount || 0), 0);

      // Use available balance from API (calculated from all-time data)
      const netTotal = parseFloat(apiData.netTotal || 0);
      const availableBalance = parseFloat(apiData.availableBalance || 0);

      // Transform commission data to match expected format
      const transformedData = {
        month: apiData.month,
        year: apiData.year,
        commissions: apiData.commissions?.map(c => ({
          loanId: c.loanId,
          loanCode: c.loan?.code || 'N/A',
          customerName: c.loan?.customerName || 'N/A',
          amount: parseFloat(c.loanAmount || 0),
          commissionPercent: parseFloat(c.commissionPercent || 0),
          commissionAmount: parseFloat(c.commissionAmount || 0),
          status: c.status,
        })) || [],
        clawbacks: apiData.clawbacks?.map(cb => ({
          loanId: cb.loanId,
          loanCode: cb.loan?.code || 'N/A',
          customerName: cb.loan?.customerName || 'N/A',
          reason: cb.reason || '',
          originalCommission: parseFloat(cb.originalCommissionAmount || 0),
          clawbackPercent: parseFloat(cb.clawbackPercent || 0),
          clawbackAmount: parseFloat(cb.clawbackAmount || 0),
        })) || [],
        totalEarned: parseFloat(apiData.totalEarned || 0),
        totalClawback: parseFloat(apiData.totalClawback || 0),
        totalPaid,
        totalPending,
        netTotal,
        availableBalance,
      };

      console.log('Transformed commission data:', transformedData);
      console.log('Available balance:', availableBalance);
      setCommissionData(transformedData);
    } catch (error) {
      console.error('Failed to fetch commission:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Mock data for development
      setCommissionData({
        month: selectedMonth,
        year: selectedYear,
        commissions: [
          {
            loanId: 1,
            loanCode: 'KV-2024-0123',
            customerName: 'Nguyễn Văn A',
            amount: 50000000,
            commissionPercent: 2.0,
            commissionAmount: 1000000,
            status: 'disbursed',
          },
          {
            loanId: 2,
            loanCode: 'KV-2024-0124',
            customerName: 'Trần Thị B',
            amount: 30000000,
            commissionPercent: 2.0,
            commissionAmount: 600000,
            status: 'disbursed',
          },
        ],
        clawbacks: [
          {
            loanId: 3,
            loanCode: 'KV-2024-0100',
            customerName: 'Lê Văn C',
            reason: 'Khách hàng trả nợ sớm (3/12 tháng)',
            originalCommission: 200000,
            clawbackPercent: 70,
            clawbackAmount: 140000,
          },
        ],
        totalEarned: 1600000,
        totalClawback: -140000,
        netTotal: 1460000,
      });
    } finally {
      setLoading(false);
    }
  };

  const months = [
    { value: 1, label: `${t('month')} 1` },
    { value: 2, label: `${t('month')} 2` },
    { value: 3, label: `${t('month')} 3` },
    { value: 4, label: `${t('month')} 4` },
    { value: 5, label: `${t('month')} 5` },
    { value: 6, label: `${t('month')} 6` },
    { value: 7, label: `${t('month')} 7` },
    { value: 8, label: `${t('month')} 8` },
    { value: 9, label: `${t('month')} 9` },
    { value: 10, label: `${t('month')} 10` },
    { value: 11, label: `${t('month')} 11` },
    { value: 12, label: `${t('month')} 12` },
  ];

  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2];

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>{t('loadingCommission')}</p>
      </div>
    );
  }

  return (
    <div className="commission-page">
      <PageHeader title={t('commissionPage')} backTo="/work" />

      <div className="page-content">
        {/* Month/Year Selector */}
        <div className="period-selector">
          <div className="selector-group">
            <Calendar size={18} />
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="period-select"
            >
              {months.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="period-select"
            >
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="summary-cards">
          <div className="summary-card success">
            <div className="card-icon">
              <TrendingUp size={24} />
            </div>
            <div className="card-content">
              <div className="card-label">{t('totalEarned')}</div>
              <div className="card-value">{formatCurrency(commissionData?.totalEarned || 0)}</div>
            </div>
          </div>

          <div className="summary-card danger">
            <div className="card-icon">
              <TrendingDown size={24} />
            </div>
            <div className="card-content">
              <div className="card-label">{t('totalClawback')}</div>
              <div className="card-value">{formatCurrency(commissionData?.totalClawback || 0)}</div>
            </div>
          </div>
        </div>

        {/* Total Paid Card */}
        {commissionData?.totalPaid > 0 && (
          <div className="summary-cards" style={{ marginTop: '0.5rem' }}>
            <div className="summary-card info" style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}>
              <div className="card-icon">
                <Send size={24} />
              </div>
              <div className="card-content">
                <div className="card-label" style={{ color: 'rgba(255,255,255,0.9)' }}>
                  {t('totalPaid')}
                </div>
                <div className="card-value" style={{ color: 'white' }}>
                  {formatCurrency(commissionData?.totalPaid || 0)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Available Balance Card */}
        <div className="summary-cards" style={{ marginTop: '0.5rem' }}>
          <div className="summary-card primary">
            <div className="card-icon">
              <DollarSign size={24} />
            </div>
            <div className="card-content">
              <div className="card-label">{t('availableBalance')}</div>
              <div className="card-value large">
                {formatCurrency(commissionData?.availableBalance || 0)}
              </div>
            </div>
          </div>
        </div>

        {/* Commissions List */}
        <div className="commission-section">
          <h2 className="section-title">
            ✅ {t('commissionsEarned')} ({commissionData?.commissions?.length || 0})
          </h2>

          {commissionData?.commissions && commissionData.commissions.length > 0 ? (
            <div className="commission-list">
              {commissionData.commissions.map((item) => (
                <div key={item.loanId} className="commission-item">
                  <div className="commission-header">
                    <span className="commission-code">{item.loanCode}</span>
                    <span className="commission-percent">{item.commissionPercent}%</span>
                  </div>

                  <div className="commission-customer">{item.customerName}</div>

                  <div className="commission-details">
                    <div className="detail-row">
                      <span className="detail-label">{t('loanAmount')}:</span>
                      <span className="detail-value">{formatCurrency(item.amount)}</span>
                    </div>
                    <div className="detail-row highlight">
                      <span className="detail-label">{t('commission')}:</span>
                      <span className="detail-value success">
                        +{formatCurrency(item.commissionAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state-small">{t('noCommissionThisMonth')}</div>
          )}
        </div>

        {/* Clawbacks */}
        {commissionData?.clawbacks && commissionData.clawbacks.length > 0 && (
          <div className="commission-section clawback-section">
            <h2 className="section-title">
              ⚠️ {t('clawbacks')} ({commissionData.clawbacks.length})
            </h2>

            <div className="clawback-list">
              {commissionData.clawbacks.map((item) => (
                <div key={item.loanId} className="clawback-item">
                  <div className="clawback-header">
                    <span className="clawback-code">{item.loanCode}</span>
                    <span className="clawback-percent">-{item.clawbackPercent}%</span>
                  </div>

                  <div className="clawback-customer">{item.customerName}</div>

                  <div className="clawback-reason">{item.reason}</div>

                  <div className="clawback-details">
                    <div className="detail-row">
                      <span className="detail-label">{t('originalCommission')}:</span>
                      <span className="detail-value">{formatCurrency(item.originalCommission)}</span>
                    </div>
                    <div className="detail-row highlight">
                      <span className="detail-label">{t('clawback')}:</span>
                      <span className="detail-value danger">
                        -{formatCurrency(item.clawbackAmount)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payment Request Button */}
        {commissionData?.availableBalance > 0 && (
          <div className="action-section">
            <button
              className="btn btn-primary btn-lg btn-block"
              onClick={() => navigate('/loan/payment-request')}
              style={{
                fontSize: '1.125rem',
                padding: '1rem 1.5rem',
                minHeight: '60px',
                fontWeight: '600'
              }}
            >
              <Send size={24} />
              {t('requestPayment')}
              <span className="btn-amount">{formatCurrency(commissionData.availableBalance)}</span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default CommissionPage;
